import { useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Check, MapPin, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useIsMobile } from "@/components/shared/PagePrimitives";

// Výběr stavby na telefonu (199, návrh B): hledání podle názvu, adresy i čísla;
// nejbližší stavba je potichu nahoře (poloha telefonu + souřadnice zakázky) —
// vzdálenost se nikde neukazuje. Bez polohy zůstává pořadí, jak přišlo.
// Počítač a tablet (236): panel, který jemně vyjede zprava; horní hranou a pravým
// okrajem lícuje s prvkem [data-site-anchor] (pruh na úvodu), jinak 16 px od okraje.

const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const dist = (a, b) => {
  const R = 6371e3, r = Math.PI / 180;
  const dLat = (b.lat - a.lat) * r, dLng = (b.lng - a.lng) * r;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
};
const stavby = (n) => (n === 1 ? "stavba" : n >= 2 && n <= 4 ? "stavby" : "staveb");

let geoCache = null;   // { lat, lng, at } — poloha na pár minut, ať se neptáme pořád
function quickPosition() {
  return new Promise((res) => {
    if (geoCache && Date.now() - geoCache.at < 5 * 60000) return res(geoCache);
    if (typeof navigator === "undefined" || !navigator.geolocation) return res(null);
    let done = false;
    const end = (v) => { if (!done) { done = true; res(v); } };
    setTimeout(() => end(null), 5000);
    try {
      navigator.geolocation.getCurrentPosition(
        (p) => { geoCache = { lat: p.coords.latitude, lng: p.coords.longitude, at: Date.now() }; end(geoCache); },
        () => end(null), { enableHighAccuracy: false, maximumAge: 300000, timeout: 4500 });
    } catch { end(null); }
  });
}

// kam panel na počítači přiložit: horní hrana a pravý okraj kotvy, když je vidět
function panelBox() {
  const el = typeof document !== "undefined" ? document.querySelector("[data-site-anchor]") : null;
  const r = el ? el.getBoundingClientRect() : null;
  const vh = window.innerHeight, vw = window.innerWidth;
  const top = r && r.top >= 8 && r.top < vh - 320 ? Math.round(r.top) : 16;
  const right = r && r.right > vw / 2 ? Math.max(8, Math.round(vw - r.right)) : 16;
  return { top, right, height: Math.min(780, vh - top - 16) };
}

export default function SitePicker({ open, onOpenChange, title = "Kde dnes pracuješ?", subtitle = null, projects = [], selectedId = null, onPick, busy = false }) {
  const mobile = useIsMobile();
  const [box, setBox] = useState(null);
  useEffect(() => { if (open && !mobile) setBox(panelBox()); }, [open, mobile]);
  const [q, setQ] = useState("");
  const [coords, setCoords] = useState({});      // id → {lat, lng}
  const [here, setHere] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setQ("");
    let alive = true;
    api.get("/projects/options").then((r) => {
      if (!alive) return;
      const m = {};
      (Array.isArray(r.data) ? r.data : r.data?.items || []).forEach((p) => {
        const lat = Number(p.lat), lng = Number(p.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng) && (lat || lng)) m[p.id] = { lat, lng, code: p.code || p.number || "" };
      });
      setCoords(m);
    }).catch(() => {});
    quickPosition().then((pos) => { if (alive) setHere(pos); });
    return () => { alive = false; };
  }, [open]);

  const rows = useMemo(() => {
    const list = projects.map((p, i) => ({ ...p, i, code: coords[p.id]?.code || p.code || "", d: here && coords[p.id] ? dist(here, coords[p.id]) : null }));
    if (here) list.sort((a, b) => (a.d ?? Infinity) - (b.d ?? Infinity) || a.i - b.i);
    const nq = norm(q.trim());
    return nq ? list.filter((p) => norm(`${p.name} ${p.address || ""} ${p.code}`).includes(nq)) : list;
  }, [projects, coords, here, q]);

  const body = (
    <>
        <div className={`px-4 pb-2 shrink-0 ${mobile ? "pt-3" : "pt-5"}`}>
          <DialogTitle className="font-heading text-[21px] font-bold pr-12">{title}</DialogTitle>
          {subtitle && <p className="mt-0.5 text-sm text-muted-foreground pr-12">{subtitle}</p>}
          <label className="mt-3 flex items-center gap-2.5 h-12 px-3.5 rounded-full bg-secondary text-muted-foreground focus-within:ring-2 focus-within:ring-brand">
            <Search size={18} aria-hidden="true" />
            <input ref={inputRef} type="search" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Hledat stavbu" data-testid="site-picker-search"
              placeholder="Hledat stavbu, adresu nebo číslo" className="flex-1 min-w-0 h-11 bg-transparent outline-none text-base text-foreground placeholder:text-muted-foreground" />
          </label>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]" role="listbox" aria-label={title}>
          {rows.length > 0 && <div className="text-xs font-bold uppercase tracking-[.08em] text-muted-foreground px-1.5 pt-3.5 pb-2">{q.trim() ? `${rows.length} ${stavby(rows.length)}` : "Moje stavby"}</div>}
          <div className="rounded-[18px] overflow-hidden">
            {rows.map((p, i) => {
              const sel = p.id === selectedId;
              return (
                <button key={p.id} type="button" role="option" aria-selected={sel} disabled={busy} onClick={() => onPick?.(p.id)} data-testid={`site-pick-${p.id}`}
                  className={`w-full min-h-[60px] flex items-center gap-3 px-3.5 py-2 text-left disabled:opacity-60 ${sel ? "bg-success/15" : "bg-background"} ${i ? "border-t border-border" : ""}`}>
                  <span className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${sel ? "bg-card text-success" : "bg-secondary text-muted-foreground"}`}><MapPin size={18} aria-hidden="true" /></span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-base font-semibold truncate">{p.name}</span>
                    <span className="block text-[13px] text-muted-foreground truncate">{[p.code, p.address].filter(Boolean).join(" · ") || "—"}</span>
                  </span>
                  {sel && <span className="w-6 h-6 rounded-full bg-success text-white grid place-items-center shrink-0"><Check size={14} strokeWidth={3} aria-hidden="true" /></span>}
                </button>
              );
            })}
          </div>
          {!rows.length && (
            <p role="status" className="mt-6 text-center text-[15px] text-muted-foreground">
              {projects.length ? `Žádná stavba neodpovídá „${q.trim()}“` : "Nejsi přiřazen k žádné aktivní stavbě."}
            </p>
          )}
        </div>
    </>
  );

  if (!mobile) {
    const b = box || { top: 16, right: 16, height: 640 };
    return (
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="pp-panel-ov fixed inset-0 z-50 bg-black/35" />
          <DialogPrimitive.Content data-testid="site-picker" aria-describedby={undefined}
            onOpenAutoFocus={(e) => { e.preventDefault(); inputRef.current?.focus(); }}
            className="pp-panel fixed z-50 flex flex-col overflow-hidden bg-card text-foreground border border-border rounded-[26px] shadow-2xl focus:outline-none"
            style={{ top: b.top, right: b.right, height: b.height, width: "min(440px, calc(100vw - 32px))" }}>
            {body}
            <DialogPrimitive.Close className="absolute right-4 top-4 w-9 h-9 rounded-full grid place-items-center bg-secondary text-muted-foreground hover:text-foreground" aria-label="Zavřít">
              <X size={18} aria-hidden="true" />
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent sheet className="max-w-lg p-0 gap-0 max-md:h-[calc(100dvh-env(safe-area-inset-top)-40px)] flex flex-col overflow-hidden"
        data-testid="site-picker" onOpenAutoFocus={(e) => e.preventDefault()}>
        {body}
      </DialogContent>
    </Dialog>
  );
}
